import React, { useState } from "react";
import { AiOutlineEdit } from "react-icons/ai";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Link, useNavigate } from "react-router-dom";
import { useToast } from "../../components/ui/use-toast";
import { ClipLoader } from "react-spinners";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
} from "../../components/ui/dialog";
import LocationForm from "../../components/LocationForm";
import { IoSearchOutline } from "react-icons/io5";
import { Input } from "../../components/ui/input";
import img from "../../pics/landImg.jpeg";
import { MdKeyboardArrowLeft } from "react-icons/md";
import { useCheckClientLocations } from "../../migration-api/api-hooks/clients";

function OnboardLocations() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const [clientLocations, setClientLocations] = useState([]);
  const [localLocation, setLocalLocation] = useState(null);
  const [clientEmail, setClientEmail] = useState("");

  // Define email validation schema
  const emailSchema = z.string().email({
	message: "Only valid email is allowed",
  });

  const handleOpen = (clientData) => {
    setOpen(true);
    setLocalLocation({ ...clientData, client_email: clientEmail });
  };

  // Use the custom hook
  const { checkClientLocations, isLoading: isChecking } =
    useCheckClientLocations({
      onSuccess: (data) => {
        const locations = data?.data?.locations || [];
        setClientLocations(locations);

        // Optional: Show success toast with count
        if (locations.length === 0) {
          toast({
            title: "No Locations Found",
            description: "This client has no locations available.",
          });
        } else {
          toast({
            variant: "success",
            title: "Success",
            description: `Found ${locations.length} location(s)`,
          });
        }
      },
      onError: (error) => {
        // Clear the locations when error occurs
        setClientLocations([]);

        if (error.code === "ERR_NETWORK") {
          navigate("/", { replace: true });
        } else if (
          error.response?.status === 401 ||
          error.response?.status === 403
        ) {
          navigate("/", { replace: true });
        } else {
          toast({
            variant: "destructive",
            title: "Error",
            description: error?.response?.data?.message || "An error occurred",
          });
        }
      },
    });

  const handleGs1Search = () => {
	if (!clientEmail || clientEmail.trim() === "") return;
	
	// Validate email using Zod
    const result = emailSchema.safeParse(clientEmail);
    
    if (!result.success) {
      toast({
        variant: "destructive",
        title: "Invalid Email",
        description: result.error.errors[0].message,
      });
      return;
    }

    checkClientLocations({ client_email: clientEmail });
  };

  const handleChange = (e) => {
    setClientEmail(e.target.value);
  };

  const isSearchDisabled = !clientEmail || clientEmail?.trim() === "";

  return (
    <section className="relative w-full min-h-screen">
      <div className="p-8">
        <div className="flex items-center justify-between">
          <h2 className="text-primary text-3xl my-5 font-medium">
            Update your locations
          </h2>

          <div className="space-y-2">
            <Link
              to={"/onboard-products"}
              className="rounded-lg px-4 py-1 bg-primary text-white flex items-center gap-2 cursor-pointer !w-fit"
              replace
            >
              <MdKeyboardArrowLeft size={18} />
              <p>Go back</p>
            </Link>
            <small>go back to products</small>
          </div>
        </div>
        <div className="mt-4">
          <p className="mb-2">Enter client email here</p>

          <div className="relative flex items-center">
            <Input
              type="email"
              placeholder={"johndoe@mail.com"}
              name="client_email"
              autoComplete="off"
              containerStyle="w-full"
              className={"pr-11"}
              onChange={handleChange}
              value={clientEmail}
            />
            <button
              type="button"
              className="absolute right-2 bg-primary text-white rounded-md p-1 disabled:bg-primary/60"
              onClick={handleGs1Search}
              disabled={isChecking || isSearchDisabled}
            >
              {isChecking ? (
                <ClipLoader color="white" size={14} />
              ) : (
                <IoSearchOutline />
              )}
            </button>
          </div>
          <small>
            click the search icon to see all your available locations, and
            update them.
          </small>
        </div>
        <div className="relative">
          {isChecking ? (
            <div className="p-[40px] h-[20vh] place-content-center text-center z-50">
              <ClipLoader color="primary" size={35} />
              <p>Loading locations</p>
            </div>
          ) : (
            <section className="mt-7">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Address</TableHead>
                    <TableHead className="text-center">Gln</TableHead>
                    <TableHead className="text-center">Latitude</TableHead>
                    <TableHead className="text-center">Longitude</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clientLocations?.length > 0 ? (
                    clientLocations?.map((location, i) => {
                      return (
                        <TableRow key={i + 1}>
                          <TableCell>{location.address}</TableCell>
                          <TableCell className="text-center">
                            {location.gln}
                          </TableCell>
                          <TableCell className="text-center">
                            {location.latitude}
                          </TableCell>
                          <TableCell className="text-center">
                            {location.longitude}
                          </TableCell>
                          <TableCell>
                            <button
                              onClick={() => {
                                handleOpen(location);
                              }}
                              className="flex items-center gap-1 text-primary"
                            >
                              <AiOutlineEdit /> edit
                            </button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <h1 className="text-center h-[20vh] place-content-center">
                          NO LOCATIONS FOUND
                        </h1>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </section>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={"max-w-xl"}>
          <DialogHeader>
            <DialogDescription>
              <LocationForm
                locationData={localLocation}
                isOnboarding={true}
                onComplete={() => {
                  setOpen(false);
                }}
              />
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      <section className="absolute bottom-0 w-full flex flex-col justify-center items-center gap-4">
        <p className=" text-primary text-[9px]">
          Copyright &copy; {new Date().getFullYear()}
        </p>
        <img src={img} alt="" className="max-h-[100px] max-w-[70dvw] mx-auto" />
      </section>
    </section>
  );
}

export default OnboardLocations;
